import React, { useCallback } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { InboxConversation } from '@/types/residents/marketplaceListing';
import { 
  BreadcrumbNavigation, 
  MarketplaceScreenLayout,
  ProfileNavDropdown,
  InboxConversationCard,
  MarketplaceEmptyState,
} from '@/components/residents/marketplace';
import { 
  MARKETPLACE_MAX_CONTENT_WIDTH,
  getResponsivePadding,
} from '@/utils/marketplace';
import { useGetConversationsQuery } from '@/store/api';

type MarketplaceInboxNavigationProp = StackNavigationProp<RootStackParamList, 'MarketplaceInbox'>;

export const MarketplaceInboxScreen: React.FC = () => {
  const navigation = useNavigation<MarketplaceInboxNavigationProp>();
  const { layoutMode, isWeb } = useResponsive();
  
  // Responsive layout detection
  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';
  const isDesktop = layoutMode === 'desktop';
  
  // Responsive values
  const contentPadding = getResponsivePadding(layoutMode);
  const maxContentWidth = isTablet 
    ? MARKETPLACE_MAX_CONTENT_WIDTH.tablet 
    : isDesktop 
      ? MARKETPLACE_MAX_CONTENT_WIDTH.desktop 
      : undefined;
  
  // Fetch conversations from API
  const { 
    data: conversations = [], 
    isLoading,
    refetch,
  } = useGetConversationsQuery();

  // Refetch conversations when screen gains focus
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleGoToMarketplace = () => {
    if (isWeb && isDesktop) {
      navigation.navigate('Marketplace' as any);
    } else {
      navigation.navigate('Main', { screen: 'ResidentMarketplace' } as any);
    }
  };

  const handleConversationPress = (conversation: InboxConversation) => {
    navigation.navigate('InboxConversation', { 
      conversationId: conversation.id,
    });
  };

  return (
    <MarketplaceScreenLayout navigatorTitle="Inbox">
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: contentPadding },
          !isMobile && styles.contentLarge,
        ]}
        showsVerticalScrollIndicator={false}
      >
        {/* Responsive content wrapper */}
        <View style={[
          styles.contentWrapper,
          maxContentWidth ? { maxWidth: maxContentWidth, alignSelf: 'center', width: '100%' } : undefined,
        ]}>
          {/* Back Navigation */}
          <BreadcrumbNavigation 
            onHomePress={handleGoToMarketplace}
            onBackPress={handleGoBack}
          />

          {/* Profile Navigation Dropdown */}
          <ProfileNavDropdown activeOption="inbox" />

          {/* Conversations List */}
          <View style={[
            styles.listContainer,
            !isMobile && styles.listContainerLarge,
          ]}>
            {isLoading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={MARKETPLACE_COLORS.accentRed} />
              </View>
            ) : conversations.length > 0 ? (
              conversations.map((conversation) => (
                <InboxConversationCard
                  key={conversation.id}
                  conversation={conversation}
                  onPress={() => handleConversationPress(conversation)}
                />
              ))
            ) : (
              <MarketplaceEmptyState
                title="No conversations yet"
                subtitle="Start a conversation by contacting a seller on a listing"
              />
            )}
          </View>
        </View>
      </ScrollView>
    </MarketplaceScreenLayout>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
    padding: 20,
    paddingBottom: 40,
    gap: 24,
  },
  contentLarge: {
    gap: 32,
    paddingBottom: 60,
  },
  contentWrapper: {
    flex: 1,
    gap: 24,
  },
  listContainer: {
    flex: 1,
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    overflow: 'hidden',
    marginBottom: -40,
    minHeight: 300,
  },
  listContainerLarge: {
    marginBottom: 0,
    borderRadius: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
});
